import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiRequest } from '../utils/api';
import { Plus, Trash2, ExternalLink, CreditCard as Edit2, Info, Globe, Server, Shield, Network } from 'lucide-react';
import Select from 'react-select';
import { Toaster, toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// Color System
const colors = {
  primary: '#2563eb',
  primaryDark: '#1d4ed8',
  primaryLight: '#3b82f6',
  secondary: '#f8fafc',
  secondaryDark: '#f1f5f9',
  accent: '#06b6d4',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  dark: '#0f172a',
  darkAlt: '#1e293b',
  darkCard: '#334155',
  text: '#64748b',
  textLight: '#94a3b8',
  textDark: '#1e293b',
  white: '#ffffff',
  border: '#e2e8f0',
  borderDark: '#475569'
};

// Main layout container
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const PageHeader = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid ${colors.border};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.textDark};
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: ${colors.text};
  margin: 0;
  line-height: 1.5;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
  gap: 1rem;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 640px) {
    justify-content: center;
  }
`;

const AddAssetButton = styled.button`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%);
  color: ${colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.25);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 320px;
  width: 100%;
  
  @media (max-width: 640px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid ${colors.border};
  background: ${colors.white};
  color: ${colors.textDark};
  font-size: 0.95rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &::placeholder {
    color: ${colors.textLight};
  }
`;

const AssetsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AssetCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  border: 1px solid ${colors.border};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.1);
    border-color: ${colors.primary};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => getTypeColor(props.type)};
    transition: all 0.3s ease;
  }
`;

const AssetCardContent = styled.div`
  padding: 1.5rem;
`;

const AssetCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const AssetTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${colors.textDark};
  margin: 0;
  flex: 1;
  margin-right: 1rem;
  line-height: 1.3;
`;

const AssetActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  color: ${colors.textLight};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${colors.secondaryDark};
    color: ${props => props.variant === 'delete' ? colors.error : colors.primary};
    transform: scale(1.05);
  }
`;

const AssetInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AssetInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: ${colors.text};
  
  svg {
    color: ${colors.textLight};
    flex-shrink: 0;
  }
  
  span {
    color: ${colors.textDark};
    font-weight: 500;
  }
`;

const AssetTypeLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: ${props => getTypeColor(props.type)}15;
  color: ${props => getTypeColor(props.type)};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid ${props => getTypeColor(props.type)}30;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  color: ${props => getStatusColor(props.status)};
  background: ${props => getStatusColor(props.status)}15;
  border: 1px solid ${props => getStatusColor(props.status)}30;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  width: 100%;
  max-width: ${props => props.wide ? '42rem' : '32rem'};
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.2);
  border: 1px solid ${colors.border};
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @media (max-width: 640px) {
    padding: 1.5rem;
    margin: 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${colors.textDark};
  line-height: 1.2;
`;

const AssetTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AssetTypeCard = styled.div`
  background: ${colors.secondary};
  border: 2px solid ${colors.border};
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, ${colors.primary}05, ${colors.primary}10);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    border-color: ${colors.primary};
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.15);
    
    &::before {
      opacity: 1;
    }
  }
`;

const AssetTypeIcon = styled.div`
  margin-bottom: 1rem;
  color: ${colors.primary};
  position: relative;
  z-index: 1;
  
  svg {
    transition: transform 0.3s ease;
  }
  
  ${AssetTypeCard}:hover & svg {
    transform: scale(1.1);
  }
`;

const AssetTypeName = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: ${colors.textDark};
  position: relative;
  z-index: 1;
`;

const AssetTypeDescription = styled.p`
  font-size: 0.875rem;
  color: ${colors.text};
  margin: 0;
  line-height: 1.4;
  position: relative;
  z-index: 1;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%);
    color: ${colors.white};
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(37, 99, 235, 0.25);
    }
  ` : `
    background: ${colors.secondary};
    color: ${colors.text};
    border: 1px solid ${colors.border};
    
    &:hover {
      background: ${colors.secondaryDark};
      border-color: ${colors.borderDark};
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: ${colors.white};
  border-radius: 16px;
  border: 1px solid ${colors.border};
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${colors.secondary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: ${colors.textLight};
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${colors.textDark};
  margin: 0 0 0.5rem 0;
`;

const EmptyStateText = styled.p`
  color: ${colors.text};
  margin: 0 0 1.5rem 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
`;

// Helper functions
function getTypeColor(type) {
  switch (type) {
    case 'Web Application':
      return colors.primary;
    case 'API Endpoint':
      return colors.accent;
    case 'Network Address':
      return colors.success;
    case 'Firewall Address':
      return colors.warning;
    default:
      return colors.primary;
  }
}

function getStatusColor(status) {
  switch (status) {
    case 'active':
      return colors.success;
    case 'scanning':
      return colors.warning;
    case 'inactive':
      return colors.error;
    default:
      return colors.textLight;
  }
}

function getTypeIcon(type) {
  switch (type) {
    case 'Web Application':
      return <Globe size={20} />;
    case 'API Endpoint':
      return <Server size={20} />;
    case 'Network Address':
      return <Network size={20} />;
    case 'Firewall Address':
      return <Shield size={20} />;
    default:
      return <Globe size={20} />;
  }
}

// Helper type-mapping functions
function mapTypeToAssetType(uiType) {
  switch (uiType) {
    case 'Web Application':
      return 'website';
    case 'API Endpoint':
      return 'api';
    case 'Firewall Address':
      return 'firewall';
    case 'Network Address':
      return 'network';
    default:
      return 'website';
  }
}

function mapAssetTypeToUI(assetType) {
  switch (assetType) {
    case 'website':
      return 'Web Application';
    case 'api':
      return 'API Endpoint';
    case 'firewall':
      return 'Firewall Address';
    case 'network':
      return 'Network Address';
    default:
      return 'Web Application';
  }
}

const AssetsPage = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTypeSelection, setShowTypeSelection] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest('/api/assets', { method: 'GET' })
      .then(async (res) => {
        if (!res.ok) {
          console.error('Failed to fetch assets:', res.status);
          return;
        }
        const data = await res.json();
        if (data.assets) {
          const mapped = data.assets.map((a) => ({
            name: a.name,
            url: a.url || '',
            type: mapAssetTypeToUI(a.assetType),
            backend: a.backend || '',
            loginUrl: a.loginSequence?.loginUrl || '',
            username: a.loginSequence?.username || '',
            password: a.loginSequence?.password || '',
            cookies: a.loginSequence?.cookies || '',
            ips: a.ips || [],
            status: 'active'
          }));
          setAssets(mapped);
        }
      })
      .catch((err) => {
        console.error('Error fetching assets:', err);
        toast.error('Failed to fetch assets', {
          style: {
            background: colors.error,
            color: colors.white,
            padding: '16px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500'
          }
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleAddButtonClick = () => {
    setShowTypeSelection(true);
  };

  const handleAssetTypeSelect = (type) => {
    setShowTypeSelection(false);
    const route = type.toLowerCase().replace(' ', '-');
    navigate(`/add-assets/${route}`);
  };

  const handleDeleteAsset = (assetName) => {
    apiRequest('/api/assets/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: assetName })
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok) {
          setAssets((prev) => prev.filter((a) => a.name !== assetName));
          toast.success(data.message || 'Asset deleted successfully', {
            style: {
              background: colors.success,
              color: colors.white,
              padding: '16px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }
          });
        } else {
          console.error('Error deleting asset:', data.message);
          toast.error(data.message || 'Failed to delete asset', {
            style: {
              background: colors.error,
              color: colors.white,
              padding: '16px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500'
            }
          });
        }
      })
      .catch((err) => {
        console.error('Error deleting asset:', err);
        toast.error('An unexpected error occurred', {
          style: {
            background: colors.error,
            color: colors.white,
            padding: '16px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500'
          }
        });
      });
  };

  const navigateToEditPage = (asset) => {
    let editRoute;
    switch (asset.type) {
      case 'Web Application':
        editRoute = 'web';
        break;
      case 'API Endpoint':
        editRoute = 'api';
        break;
      case 'Network Address':
        editRoute = 'network';
        break;
      case 'Firewall Address':
        editRoute = 'firewall';
        break;
      default:
        editRoute = 'web';
    }

    navigate(`/edit-assets/${editRoute}`, {
      state: { asset }
    });
  };

  const filteredAssets = assets.filter((asset) => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const ipsString = asset.ips.join(' ').toLowerCase();
    return (
      asset.name.toLowerCase().includes(lowerSearchTerm) ||
      asset.url.toLowerCase().includes(lowerSearchTerm) ||
      asset.type.toLowerCase().includes(lowerSearchTerm) ||
      ipsString.includes(lowerSearchTerm)
    );
  });

  if (loading) {
    return (
      <PageWrapper>
        <ContentContainer>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px',
            color: colors.text 
          }}>
            Loading assets...
          </div>
        </ContentContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentContainer>
        {/* <PageHeader>
          <PageTitle>Asset Management</PageTitle>
          <PageSubtitle>
            Manage and monitor your security assets including web applications, APIs, and network resources
          </PageSubtitle>
          
          <HeaderControls>
            <ControlsLeft>
              <AddAssetButton onClick={handleAddButtonClick}>
                <Plus size={20} />
                Add New Asset
              </AddAssetButton>
            </ControlsLeft>
            
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>
          </HeaderControls>
        </PageHeader> */}

        {filteredAssets.length === 0 && !loading ? (
          <EmptyState>
            <EmptyStateIcon>
              <Server size={28} />
            </EmptyStateIcon>
            <EmptyStateTitle>No Assets Found</EmptyStateTitle>
            <EmptyStateText>
              {searchTerm ? 
                'No assets match your search criteria. Try adjusting your search terms.' :
                'Get started by adding your first asset to begin monitoring your security posture.'
              }
            </EmptyStateText>
            {!searchTerm && (
              <AddAssetButton onClick={handleAddButtonClick}>
                <Plus size={20} />
                Add Your First Asset
              </AddAssetButton>
            )}
          </EmptyState>
        ) : (
          <AssetsGrid>
            {filteredAssets.map((asset) => (
              <AssetCard key={asset.name} type={asset.type}>
                <AssetCardContent>
                  <AssetCardHeader>
                    <AssetTitle>{asset.name}</AssetTitle>
                    <AssetActions>
                      <ActionButton onClick={() => navigateToEditPage(asset)}>
                        <Edit2 size={18} />
                      </ActionButton>
                      <ActionButton 
                        variant="delete"
                        onClick={() => handleDeleteAsset(asset.name)}
                      >
                        <Trash2 size={18} />
                      </ActionButton>
                    </AssetActions>
                  </AssetCardHeader>

                  <AssetInfo>
                    {(asset.type === 'Firewall Address' || asset.type === 'Network Address') ? (
                      <AssetInfoItem>
                        <Network size={16} />
                        <span>
                          {asset.ips && asset.ips.length ? asset.ips.join(', ') : 'No IPs configured'}
                        </span>
                      </AssetInfoItem>
                    ) : (
                      <AssetInfoItem>
                        <ExternalLink size={16} />
                        <span>{asset.url || 'No URL configured'}</span>
                      </AssetInfoItem>
                    )}
                    
                    <AssetInfoItem>
                      <AssetTypeLabel type={asset.type}>
                        {getTypeIcon(asset.type)}
                        {asset.type}
                      </AssetTypeLabel>
                    </AssetInfoItem>
                    
                    <AssetInfoItem>
                      <span style={{ fontSize: '0.875rem', color: colors.textLight }}>Status:</span>
                      <StatusBadge status={asset.status}>
                        {asset.status}
                      </StatusBadge>
                    </AssetInfoItem>
                  </AssetInfo>
                </AssetCardContent>
              </AssetCard>
            ))}
          </AssetsGrid>
        )}

        {/* Asset Type Selection Modal */}
        {showTypeSelection && (
          <ModalOverlay>
            <ModalContent>
              <ModalTitle>Select Asset Type</ModalTitle>
              <p style={{ color: colors.text, marginBottom: '1.5rem', lineHeight: 1.5 }}>
                Choose the type of asset you want to add to your security monitoring dashboard.
              </p>
              
              <AssetTypeGrid>
                <AssetTypeCard onClick={() => handleAssetTypeSelect('web')}>
                  <AssetTypeIcon>
                    <Globe size={32} />
                  </AssetTypeIcon>
                  <AssetTypeName>Web Application</AssetTypeName>
                  <AssetTypeDescription>
                    Websites and web applications with HTML interfaces
                  </AssetTypeDescription>
                </AssetTypeCard>

                <AssetTypeCard onClick={() => handleAssetTypeSelect('api')}>
                  <AssetTypeIcon>
                    <Server size={32} />
                  </AssetTypeIcon>
                  <AssetTypeName>API Endpoint</AssetTypeName>
                  <AssetTypeDescription>
                    REST, GraphQL, or other API endpoints
                  </AssetTypeDescription>
                </AssetTypeCard>

                <AssetTypeCard onClick={() => handleAssetTypeSelect('network')}>
                  <AssetTypeIcon>
                    <Network size={32} />
                  </AssetTypeIcon>
                  <AssetTypeName>Network Address</AssetTypeName>
                  <AssetTypeDescription>
                    IP addresses and network ranges
                  </AssetTypeDescription>
                </AssetTypeCard>

                <AssetTypeCard onClick={() => handleAssetTypeSelect('firewall')}>
                  <AssetTypeIcon>
                    <Shield size={32} />
                  </AssetTypeIcon>
                  <AssetTypeName>Firewall Address</AssetTypeName>
                  <AssetTypeDescription>
                    Firewall rules and configurations
                  </AssetTypeDescription>
                </AssetTypeCard>
              </AssetTypeGrid>

              <ModalButtons>
                <ModalButton onClick={() => setShowTypeSelection(false)}>
                  Cancel
                </ModalButton>
              </ModalButtons>
            </ModalContent>
          </ModalOverlay>
        )}

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              padding: '16px',
            }
          }}
        />
      </ContentContainer>
    </PageWrapper>
  );
};

export default AssetsPage;